import { useEffect, useState } from "react";
import { ArrowUpRight, Calendar, MapPin, Clock, Video, ExternalLink } from "lucide-react";
import { getFormations } from "@/lib/api";
import { applyJsonLd } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { InscriptionModal } from "@/components/InscriptionModal";


const BACKEND = process.env.REACT_APP_BACKEND_URL;
const filtres = [
  { id: "toutes", label: "Toutes" },
  { id: "gratuites", label: "Gratuites" },
  { id: "payantes", label: "Payantes" },
];

const fmtPrix = (n) => n.toLocaleString("fr-CA");

const etiquettePrix = (f) =>
  f.passee ? "Déjà offerte" : f.prix == null ? "Prix sur Eventbrite" : f.prix === 0 ? "Gratuite" : `${fmtPrix(f.prix_effectif ?? f.prix)} $`;

const dateLimiteFr = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });

const estEnLigne = (f) => f.format.toLowerCase().includes("ligne") || f.format.toLowerCase().includes("hybride");
const estPresentiel = (f) => f.format.toLowerCase().includes("sentiel") || f.format.toLowerCase().includes("hybride");

const MOIS_FR = { janvier: 1, février: 2, mars: 3, avril: 4, mai: 5, juin: 6, juillet: 7, août: 8, septembre: 9, octobre: 10, novembre: 11, décembre: 12 };

const debutSessionISO = (session) => {
  const m = session?.match(/(\d{1,2})\s+([A-Za-zàâäéèêëîïôöùûüç]+)\s+(\d{4})[,\sà]*?(\d{1,2})\s*h/i);
  if (!m) return null;
  const mois = MOIS_FR[m[2].toLowerCase()];
  if (!mois) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const fuseau = mois >= 4 && mois <= 10 ? "-04:00" : "-05:00"; // Montréal : HAE en été, HNE en hiver
  return `${m[3]}-${pad(mois)}-${pad(m[1])}T${pad(m[4])}:00:00${fuseau}`;
};

const dureeISO = (d) => {
  const h = d?.match(/(\d+(?:[.,]\d+)?)\s*h\b/i);
  if (h) return `PT${h[1].replace(",", ".")}H`;
  const min = d?.match(/(\d+)\s*min/i);
  if (min) return `PT${min[1]}M`;
  return null;
};

// Balisage « Course » de Google : reprend uniquement les données affichées sur la page.
const jsonLdFormations = (formations) => {
  const aVenir = formations.filter((f) => !f.passee);
  if (!aVenir.length) return null;
  return {
    "@context": "https://schema.org",
    "@graph": aVenir.map((f) => {
      const debut = debutSessionISO(f.prochaine_session);
      const duree = dureeISO(f.duree);
      return {
        "@type": "Course",
        name: f.titre,
        description: f.description.length > 300 ? `${f.description.slice(0, 297)}...` : f.description,
        provider: { "@type": "Organization", name: "Fondation HORA", sameAs: "https://fondationhora.org" },
        hasCourseInstance: {
          "@type": "CourseInstance",
          courseMode: f.format.toLowerCase().includes("ligne") ? "online" : "onsite",
          ...(debut ? { startDate: debut } : {}),
          ...(duree ? { courseWorkload: duree } : {}),
        },
        ...(f.prix != null
          ? {
              offers: {
                "@type": "Offer",
                category: f.prix > 0 ? "Paid" : "Free",
                price: (f.prix_effectif ?? f.prix).toFixed(2),
                priceCurrency: "CAD",
                availability: "https://schema.org/InStock",
                url: "https://fondationhora.org/formations",
              },
            }
          : {}),
      };
    }),
  };
};

export default function Formations() {
  const [formations, setFormations] = useState([]);
  const [filtre, setFiltre] = useState("toutes");
  const [chargement, setChargement] = useState(true);
  const [inscription, setInscription] = useState(null);

  useEffect(() => {
    getFormations()
      .then(setFormations)
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => {
    applyJsonLd("formations", jsonLdFormations(formations));
    return () => applyJsonLd("formations", null);
  }, [formations]);

  const visibles = formations.filter((f) =>
    filtre === "toutes" ? true : filtre === "gratuites" ? f.prix === 0 : f.prix != null && f.prix > 0
  );

  return (
    <>
      <section className="bg-espresso py-28 text-ivory" data-testid="formations-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Formations</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="formations-title">
              Des savoirs rigoureux, à portée de <span className="italic text-primary">toutes les bourses</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Inscrivez-vous directement sur le site : pour les formations payantes, le paiement se
              fait en ligne par PayPal et vous recevez immédiatement un courriel de confirmation
              avec tous les détails — adresse ou lien Zoom inclus.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="formations-list-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-wrap gap-3" data-testid="formations-filters">
            {filtres.map((f) => (
              <button
                key={f.id}
                onClick={() => setFiltre(f.id)}
                data-testid={`filter-${f.id}`}
                className={`rounded-sm border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                  filtre === f.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground/70 hover:border-primary hover:text-primary"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {chargement ? (
            <p className="mt-16 text-foreground/50" data-testid="formations-loading">Chargement des formations…</p>
          ) : (
            <div className="mt-12" data-testid="formations-list">
              {visibles.map((f, i) => (
                <Reveal key={f.id} delay={Math.min(i * 0.04, 0.3)}>
                  <div className="grid gap-6 border-t border-border py-10 lg:grid-cols-12" data-testid={`formation-card-${i}`}>
                    <div className="lg:col-span-7">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{f.categorie}</span>
                        <span className={`rounded-sm px-3 py-1 text-xs font-semibold ${f.prix === 0 ? "bg-gold/20 text-[hsl(40,60%,32%)]" : "bg-muted text-foreground/70"}`} data-testid={`formation-price-${i}`}>
                          {etiquettePrix(f)}
                        </span>
                        {f.reduction_active && f.prix_effectif != null && (
                          <span className="text-xs text-foreground/50" data-testid={`formation-reduction-${i}`}>
                            <span className="line-through">{fmtPrix(f.prix)} $</span> — {f.reduction_active.pourcentage} % jusqu'au {dateLimiteFr(f.reduction_active.date_limite)}
                          </span>
                        )}
                      </div>
                      <h2 className="mt-4 font-serif text-3xl font-semibold leading-snug">{f.titre}</h2>
                      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground/60 md:text-base">{f.description}</p>
                      {f.passee && f.video_path && (
                        <>
                          <video
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            src={`${BACKEND}/api/media/${f.video_path}`}
                            className="mt-6 aspect-video w-full border border-border bg-espresso"
                            data-testid={`formation-video-${i}`}
                          />
                          <p className="mt-3 text-sm italic text-foreground/60" data-testid={`formation-video-note-${i}`}>
                            Pour plus, inscrivez-vous à nos formations.
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col justify-between gap-6 lg:col-span-4 lg:col-start-9">
                      <ul className="space-y-3 text-sm text-foreground/60">
                        <li className="flex items-center gap-3"><Calendar size={16} className="shrink-0 text-primary" /> Session : {f.prochaine_session}</li>
                        <li className="flex items-center gap-3"><Clock size={16} className="shrink-0 text-primary" /> Durée : {f.duree}</li>
                        {!f.passee && estPresentiel(f) && (
                          <li className="flex items-start gap-3" data-testid={`formation-adresse-${i}`}>
                            <MapPin size={16} className="mt-0.5 shrink-0 text-primary" />
                            <span>{f.adresse || "En présentiel — adresse confirmée à l'inscription"}</span>
                          </li>
                        )}
                        {!f.passee && estEnLigne(f) && (
                          <li className="flex items-start gap-3" data-testid={`formation-zoom-${i}`}>
                            <Video size={16} className="mt-0.5 shrink-0 text-primary" />
                            <span>En ligne — le lien Zoom est envoyé après l'inscription</span>
                          </li>
                        )}
                      </ul>
                      {f.passee ? (
                        <span
                          data-testid={`formation-inscription-${i}`}
                          className="w-fit rounded-sm bg-muted px-6 py-3 text-sm font-semibold text-foreground/50"
                        >
                          Formation déjà offerte
                        </span>
                      ) : f.lien ? (
                        <a
                          href={f.lien}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`formation-inscription-${i}`}
                          className="group flex w-fit items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
                        >
                          S'inscrire sur Eventbrite
                          <ExternalLink size={16} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </a>
                      ) : (
                        <button
                          onClick={() => setInscription(f)}
                          data-testid={`formation-inscription-${i}`}
                          className="group flex w-fit items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
                        >
                          {f.prix ? `S'inscrire — ${(f.prix_effectif ?? f.prix).toLocaleString("fr-CA")} $` : "S'inscrire"}
                          <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-border" />
              {visibles.length === 0 && (
                <p className="py-16 text-foreground/50" data-testid="formations-empty">Aucune formation dans cette catégorie pour le moment.</p>
              )}
            </div>
          )}

          <Reveal className="mt-16">
            <div className="border border-border bg-card p-10" data-testid="formations-note">
              <p className="text-sm leading-relaxed text-foreground/70">
                <span className="font-semibold text-foreground">Note :</span> les formations de la
                Clinique juridique d'affaires (ancien nom de la Fondation HORA) s'inscrivent via
                Eventbrite; les autres se réservent directement ici, avec paiement PayPal sécurisé
                pour les formations payantes. Des places gratuites sont toujours réservées aux
                personnes en situation de vulnérabilité — écrivez-nous via le formulaire de contact
                si le tarif est un obstacle.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {inscription && (
        <InscriptionModal formation={inscription} onClose={() => setInscription(null)} />
      )}
    </>
  );
}
